import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import ArrowRight from 'components/Icons/ArrowRight'
import Lock from 'components/Icons/Lock'
import WebhookSetup from 'components/Icons/WebhookSetup'
import {
  INLINE_LINK_OVERRIDES,
  LINK_ARROW_GAP,
} from 'constants/inlineLinkButton'
import palette from 'constants/palette'
import React from 'react'

const CARD_PADDING = '28px'
const CARD_RADIUS = '8px'
const BODY_GAP = '14px'
const HEADER_GAP = '8px'
const INTRO_GAP = '4px'
const ROW_LIST_GAP = '12px'
const ROW_ITEM_GAP = '8px'

export interface LockedCardRow {
  key: string
  label: string
}

// The four disabled status groups the FR-76 billboard lists (Figma order).
// Static keys under `live_updates.locked.unsupported_groups` — shared by
// every surface that renders the unsupported-ordering billboard (the
// Configuration tab's full-width version and the Overview tab's setup
// preview) so they can never list the groups differently.
export const UNSUPPORTED_GROUP_KEYS = [
  'placed_preparation',
  'ready',
  'delivered',
  'canceled',
] as const

export interface LockedCardAction {
  label: string
  /** Accessible name, when the visible label alone is ambiguous — two gate
   * cards can sit on the same page with the same "Set up" label. */
  ariaLabel?: string
  onClick: () => void
}

export interface LockedCardProps {
  /** Bold, centered lock-headline copy. */
  title: string
  /** Heading element for the title — matches the card's position in the page
   * outline (the FR-76 billboard is a top-level h2, the FR-75 section card
   * sits under its section's h2 as an h3). */
  headingTag?: 'h2' | 'h3'
  /** Header glyph: a padlock for a section that can never be turned on here,
   * the webhook-setup mark for one the merchant can unlock by connecting. */
  headerIcon?: 'lock' | 'webhook'
  /** Explanatory body copy under the header. */
  body: string
  /** Optional call to action under the body copy. Omitted when the merchant
   * has nothing to do — a link with no handler is a dead-looking control. */
  action?: LockedCardAction
  /** The disabled/suppressed rows, each rendered with its own lock icon. */
  rows: LockedCardRow[]
}

// Shared lock-card treatment (Figma R4 936:4245 / 1000:4376): a grey rounded
// card with a centered header, explanatory body, and a list of locked rows.
// Used by the FR-75 delivery lock (StatusSection), the FR-76
// unsupported-ordering billboard (the scene), and the actionable
// connect-Olo-webhooks card (Figma 2518:36887 / 2536:40630), which adds the
// set-up link on top of the same skeleton.
const LockedCard = ({
  title,
  headingTag = 'h3',
  headerIcon = 'lock',
  body,
  action,
  rows,
}: LockedCardProps) => {
  const [css] = useStyletron()

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: BODY_GAP,
        padding: CARD_PADDING,
        borderRadius: CARD_RADIUS,
        // Figma 936:5377: grey card with a hairline border so it reads as a
        // bounded surface against the page rather than a borderless fill.
        border: `1px solid ${palette.grey20}`,
        backgroundColor: palette.grey10,
      })}
    >
      <div
        className={css({
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: HEADER_GAP,
        })}
      >
        <span
          aria-hidden="true"
          className={css({ display: 'flex', flexShrink: 0 })}
        >
          {headerIcon === 'webhook' ? (
            <WebhookSetup />
          ) : (
            <Lock fillColor={palette.grey90} />
          )}
        </span>
        <Text.Header4 tag={headingTag} className="m-0">
          {title}
        </Text.Header4>
      </div>

      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: INTRO_GAP,
        })}
      >
        <Text.BodyText4 color="grey70" className={css({ display: 'block' })}>
          {body}
        </Text.BodyText4>
        {action && (
          <Button
            kind="minimal"
            aria-label={action.ariaLabel}
            onClick={action.onClick}
            overrides={INLINE_LINK_OVERRIDES}
          >
            {action.label}
            {/* Decorative: the label already says what the link does, so the
                glyph stays out of the accessible name. From the icon library
                rather than a literal arrow — raw Unicode in JSX is not
                allowed. */}
            <span
              aria-hidden="true"
              className={css({
                display: 'inline-flex',
                alignItems: 'center',
                marginLeft: LINK_ARROW_GAP,
              })}
            >
              <ArrowRight />
            </span>
          </Button>
        )}
      </div>

      <ul
        className={css({
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: ROW_LIST_GAP,
        })}
      >
        {rows.map(row => (
          <li
            key={row.key}
            className={css({
              display: 'flex',
              alignItems: 'center',
              gap: ROW_ITEM_GAP,
            })}
          >
            <span
              aria-hidden="true"
              className={css({ display: 'flex', flexShrink: 0 })}
            >
              <Lock fillColor={palette.grey70} />
            </span>
            <Text.BodyText4 color="grey70">{row.label}</Text.BodyText4>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default LockedCard
